<template>
  <div class="d-flex flex-column gap-4" data-testid="commentary-main-tab">
    <h3 class="mt-2 d-flex gap-2 align-center" v-if="!bulksubmit && contextType !== 'glbreakdown' && !hideSummarySection">
      <span>{{ selectedcommentary.__code }}</span>
      <span>{{ glCodeName }}</span>
    </h3>

    <!-- Bulk submit section - only show for multi-select bulk operations -->
    <div v-else-if="bulksubmit && contextType !== 'glbreakdown'">
      <div class="title my-2">
        <span>{{ multiselectdata.length }}</span>
        Selected
      </div>
      <div class="pb-2" v-if="bulksubmit && ispreparer">
        <!-- Show "Add commentary to non-mandatory variance" only when there are non-mandatory variances in selection -->
        <v-checkbox
          v-if="isGlMandatoryRequestFlag"
          hide-details
          class="pa-0 ma-0"
          dense
          v-model="bulkcommentallModel"
          label="Add commentary to non-mandatory variance?"
          data-testid="commentary-main-tab__bulk-comment-all"
        ></v-checkbox>
        <v-checkbox
          v-if="replaceExistingCommentFlag"
          hide-details
          class="pa-0 ma-0"
          dense
          v-model="bulkcommentreplaceModel"
          label="Replace existing comments?"
          data-testid="commentary-main-tab__bulk-comment-replace-existing"
        ></v-checkbox>
      </div>
      <div class="mb-4" v-if="bulkcommentallModel && ispreparer">
        <div>{{ commentary_local_key_label }} Voluntary commentary submission reason</div>
        <v-checkbox
          hide-details
          class="pa-0 ma-0"
          dense
          v-model="requestcommentarylocalcontrolModel"
          label="For the purpose of controls?"
          data-testid="commentary-main-tab__request-local-control"
        ></v-checkbox>
        <v-checkbox
          hide-details
          class="pa-0 ma-0"
          dense
          v-model="requestcommentarylocalmanagementModel"
          label="For the purpose of management?"
          data-testid="commentary-main-tab__request-local-management"
        ></v-checkbox>
      </div>
      <div class="mb-4" v-if="bulkcommentallModel && isapprover">
        <div>{{ commentary_regional_key_label }} Voluntary commentary submission reason</div>
        <v-checkbox
          hide-details
          class="pa-0 ma-0"
          dense
          v-model="requestcommentaryregionalcontrolModel"
          label="For the purpose of controls?"
          data-testid="commentary-main-tab__request-regional-control"
        ></v-checkbox>
        <v-checkbox
          hide-details
          class="pa-0 ma-0"
          dense
          v-model="requestcommentaryregionalmanagementModel"
          label="For the purpose of management?"
          data-testid="commentary-main-tab__request-regional-management"
        ></v-checkbox>
      </div>
    </div>

    <!-- Local Commentary Sections - Hidden during bulk approve -->
    <div v-if="!isBulkApprove" :class="{ 'section-disabled': isLocalCommentaryDisabled }">
      <div class="d-flex gap-4">
      <div class="d-flex flex-column gap-4" style="width:50%">
        <!-- Hide header and counts for glbreakdown context or when hideSummarySection is true - only show text areas -->
        <div v-if="!bulksubmit && contextType !== 'glbreakdown' && !hideSummarySection" class="pt-2 d-flex flex-column gap-2">
          <h5 class="font-size-md noTransform">
            {{ cycletype === 'Forecast' ? forecastLabel : actualLabel }}
            Vs {{ proformaLabel }}
          </h5>
          <div
            class="d-flex"
            v-if="selectedcommentary.var1_cross_control || selectedcommentary.var1_cross_management || selectedcommentary.var1_cross_control_regional || selectedcommentary.var1_cross_management_regional"
          >
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var1_cross_control">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="error--text mr-1"
                  >mdi-circle</v-icon
                >
              </template>
              <span>{{ commentary_local_key_label + ' Control Materiality crossed' }}</span>
            </v-tooltip>
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var1_cross_management">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="blue--text mr-1"
                  >mdi-circle</v-icon
                >
              </template>
              <span>{{ commentary_local_key_label + ' Management Materiality crossed' }}</span>
            </v-tooltip>
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var1_cross_control_regional">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="error--text mr-1"
                  >mdi-record-circle-outline</v-icon
                >
              </template>
              <span>{{ commentary_regional_key_label + ' Control Materiality crossed' }}</span>
            </v-tooltip>
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var1_cross_management_regional">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="blue--text mr-1"
                  >mdi-record-circle-outline</v-icon
                >
              </template>
              <span>{{ commentary_regional_key_label + ' Management Materiality crossed' }}</span>
            </v-tooltip>
          </div>
          <div class="d-flex" v-else>
            <div class="d-flex"></div>
          </div>
          <div class="d-flex gap-4">
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text">
                {{ cycletype === 'Forecast' ? forecastLabel : actualLabel }}
              </div>
              <div class="font-size-md">{{ $nova.formatCurrency(selectedcommentary.amount1, '', false, true) }}</div>
            </div>
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text">
                {{ proformaLabel }}
              </div>
              <div class="font-size-md">{{ $nova.formatCurrency(selectedcommentary.amount2, '', false, true) }}</div>
            </div>
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text noTransform">
                {{ $nova.labelsName('cycle_page', 'commentary_variance_amount_label', 'Variance Amount') }}
              </div>
              <div class="font-size-md">
                <span
                  :class="
                    selectedcommentary.varianceamount1 > 0
                      ? 'success--text font-weight-semibold'
                      : selectedcommentary.varianceamount1 < 0
                      ? 'error--text font-weight-semibold'
                      : ''
                  "
                >
                  {{ $nova.formatCurrency(selectedcommentary.varianceamount1, '', false, true) }}
                </span>
              </div>
            </div>
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text noTransform">
                {{ $nova.labelsName('cycle_page', 'commentary_variance_amount_label', 'Variance %') }}
              </div>
              <div class="font-size-md">
                <span
                  :class="
                    selectedcommentary.varianceamount1 > 0
                      ? 'success--text font-weight-semibold'
                      : selectedcommentary.varianceamount1 < 0
                      ? 'error--text font-weight-semibold'
                      : ''
                  "
                >
                  {{ $nova.formatCurrency(selectedcommentary.variancepercent1, '', false, true) }}{{ selectedcommentary.variancepercent1 !== 0 ? ' %' : '' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="bulksubmit && bulktype === 'addlocal'" class="font-size-md pl-2 border-left-custom noTransform" :style="{ '--custom-border-color': tablecolors.heading.a }">
          {{ (cycletype === 'Forecast' ? forecastLabel : actualLabel) + ' Vs ' + proformaLabel }}
        </div>

        <div class="accordion-section" ref="local_proforma_section" :class="{ 'accordion-disabled': isLocalCommentaryDisabled }">
          <div class="accordion-header d-flex align-center pa-2" :class="{ 'cursor-pointer': !isLocalCommentaryDisabled, 'cursor-not-allowed': isLocalCommentaryDisabled }" @click="!isLocalCommentaryDisabled && (localCommentaryProformaExpanded = !localCommentaryProformaExpanded)" data-testid="commentary-main-tab__local-commentary-proforma-header">
            <span class="font-weight-semibold">{{ commentary_local_key_label }} Commentary (Vs {{ proformaLabel }})</span>
            <v-spacer></v-spacer>
            <div
                class="d-flex align-center mr-2"
                v-if="
                  $store.state.aiEnabled &&
                  localCommentaryGenAiBtntab &&
                  (selectedcommentary.__myaction === 'localadd' ||
                    selectedcommentary.__myaction === 'regionaladd' ||
                    selectedcommentary.__myaction === 'localvoluntary' ||
                    selectedcommentary.__myaction === 'regionalvoluntary' ||
                    (ispreparer && bulksubmit))
                "
                @click.stop
              >
                <v-btn
                  color="primary"
                  :disabled="selectedcommentary.__stage !== 0 || isTyping || loadingtext1 || loadingtext2 || isThresholdExceedLocalCommentary"
                  :loading="loadingtext1"
                  @click.stop="handleGenerateAi('One', 'proforma')"
                  data-testid="commentary-main-tab__gen-ai-proforma"
                >
                  <v-icon dark left small>mdi-creation</v-icon>
                  <span class="noTransform">Gen AI</span>
                </v-btn>
                <v-tooltip right content-class="tooltip-right" v-if="isThresholdExceedLocalCommentary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="ml-2">mdi-help-circle-outline</v-icon>
                  </template>
                  <div class="text-center">Hour limit exceeded : Please contact your administrator to increase the limit.</div>
                </v-tooltip>
             </div>
            <v-icon class="mr-2" size="20">{{ localCommentaryProformaExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-show="localCommentaryProformaExpanded" class="accordion-content pa-3">
              <div
                v-if="showLocalProformaSave"
                class="commentary-save-card position-relative"
                data-testid="commentary-main-tab__local-proforma-save-card"
              >
                <lb-textarea
                  :class="['commentary1-textarea', {'shake-error error-highlight': shakingFields['local_proforma']}]"
                  :hidedetails="false"
                  v-model="selectedcommentary.commentary1"
                  :error-messages="persistentErrors['local_proforma']"
                  :label="
                    selectedcommentary.var1_cross_management || selectedcommentary.var1_cross_control || bulksubmit
                      ? commentary_local_key_label + ' Commentary Mandatory'
                      : commentary_local_key_label + ' Commentary Optional'
                  "
                  :required="selectedcommentary.var1_cross_management || selectedcommentary.var1_cross_control || bulksubmit"
                  :disabled="workflowtext.includes('Local control proforma') && workflowtext.includes('Local management proforma')"
                  data-testid="commentary-main-tab__local-commentary-proforma"
                />
               
                <v-btn
                  icon
                  small
                  class="commentary-save-btn"
                  :disabled="!localCommentary1CanSave"
                  :loading="saveLocalCommentaryLoading && saveLocalCommentaryField === 'proforma'"
                  data-testid="commentary-main-tab__local-proforma-save-btn"
                  @click="$emit('save-local-commentary', 'proforma')"
                >
                  <v-icon :color="localCommentary1CanSave ? 'primary' : 'grey lighten-1'">mdi-content-save</v-icon>
                </v-btn>
              </div>
              <lb-textarea
                v-else-if="((bulksubmit && bulktype === 'addlocal') || selectedcommentary.__myaction === 'localadd' || selectedcommentary.__myaction === 'localvoluntary') && selectedcommentary.__stage !== 9"
                :class="['commentary1-textarea', {'shake-error error-highlight': shakingFields['local_proforma']}]"
                :hidedetails="false"
                v-model="selectedcommentary.commentary1"
                :error-messages="persistentErrors['local_proforma']"
                :label="
                  selectedcommentary.var1_cross_management || selectedcommentary.var1_cross_control || bulksubmit
                    ? commentary_local_key_label + ' Commentary Mandatory'
                    : commentary_local_key_label + ' Commentary Optional'
                "
                :required="selectedcommentary.var1_cross_management || selectedcommentary.var1_cross_control || bulksubmit"
                :disabled="workflowtext.includes('Local control proforma') && workflowtext.includes('Local management proforma')"
                data-testid="commentary-main-tab__local-commentary-proforma"
              />
              <div v-else-if="!bulksubmit">
                <template v-if="isActualJVCommentaryValidForLocal">
                  <div class="d-flex gap-1 mb-1">
                    <v-chip small density="comfortable">JV:</v-chip>
                    <div class="commentary-text">{{ displayJvCommentary(selectedcommentary.jv_commentary1) }}</div>
                  </div>
                </template>
                <div v-else-if="selectedcommentary.commentary1 || selectedcommentary.savedcommentary1" class="commentary-text">{{ selectedcommentary.commentary1 || selectedcommentary.savedcommentary1 }}</div>
                <div class="grey--text" v-else>No Data</div>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </div>

      <div class="d-flex flex-column gap-4" style="width:50%">
        <!-- Hide header and counts for glbreakdown context or when hideSummarySection is true - only show text areas -->
        <div v-if="!bulksubmit && contextType !== 'glbreakdown' && !hideSummarySection" class="pt-2 d-flex flex-column gap-2">
          <h5 class="font-size-md noTransform">
            {{ cycletype === 'Forecast' ? forecastLabel : actualLabel }}
            Vs {{ cycletype === 'Forecast' ? 'Previous ' + forecastLabel : forecastLabel }}
          </h5>
          <div
            class="d-flex"
            v-if="selectedcommentary.var2_cross_control || selectedcommentary.var2_cross_management || selectedcommentary.var2_cross_control_regional || selectedcommentary.var2_cross_management_regional"
          >
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var2_cross_control">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="error--text mr-1"
                  >mdi-circle</v-icon
                >
              </template>
              <span>{{ commentary_local_key_label + ' Control Materiality crossed' }}</span>
            </v-tooltip>
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var2_cross_management">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="blue--text mr-1"
                  >mdi-circle</v-icon
                >
              </template>
              <span>{{ commentary_local_key_label + ' Management Materiality crossed' }}</span>
            </v-tooltip>
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var2_cross_control_regional">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="error--text mr-1"
                  >mdi-record-circle-outline</v-icon
                >
              </template>
              <span>{{ commentary_regional_key_label + ' Control Materiality crossed' }}</span>
            </v-tooltip>
            <v-tooltip left content-class="tooltip-left" v-if="selectedcommentary.var2_cross_management_regional">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" style="font-size:12px !important;" class="blue--text mr-1"
                  >mdi-record-circle-outline</v-icon
                >
              </template>
              <span>{{ commentary_regional_key_label + ' Management Materiality crossed' }}</span>
            </v-tooltip>
          </div>
          <div class="d-flex" v-else>
            <div class="d-flex"></div>
          </div>
          <div class="d-flex gap-4">
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text">
                {{ cycletype === 'Forecast' ? forecastLabel : actualLabel }}
              </div>
              <div class="font-size-md">{{ $nova.formatCurrency(selectedcommentary.amount1, '', false, true) }}</div>
            </div>
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text">
                {{ cycletype === 'Forecast' ? 'Previous ' + forecastLabel : forecastLabel }}
              </div>
              <div class="font-size-md">{{ $nova.formatCurrency(selectedcommentary.amount3, '', false, true) }}</div>
            </div>
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text noTransform">
                {{ $nova.labelsName('cycle_page', 'commentary_variance_amount_label', 'Variance Amount') }}
              </div>
              <div class="font-size-md">
                <span
                  :class="
                    selectedcommentary.varianceamount2 > 0
                      ? 'success--text font-weight-semibold'
                      : selectedcommentary.varianceamount2 < 0
                      ? 'error--text font-weight-semibold'
                      : ''
                  "
                >
                  {{ $nova.formatCurrency(selectedcommentary.varianceamount2, '', false, true) }}
                </span>
              </div>
            </div>
            <div class="d-flex flex-column gap-1">
              <div class="font-weight-semibold grey--text noTransform">
                {{ $nova.labelsName('cycle_page', 'commentary_variance_amount_label', 'Variance %') }}
              </div>
              <div class="font-size-md">
                <span
                  :class="
                    selectedcommentary.varianceamount2 > 0
                      ? 'success--text font-weight-semibold'
                      : selectedcommentary.varianceamount2 < 0
                      ? 'error--text font-weight-semibold'
                      : ''
                  "
                >
                  {{ $nova.formatCurrency(selectedcommentary.variancepercent2, '', false, true) }}{{ selectedcommentary.variancepercent2 !== 0 ? ' %' : '' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="bulksubmit && bulktype === 'addlocal'" class="font-size-md pl-2 noTransform" :style="`border-left: 4px solid ${tablecolors.heading.b} !important`">
          {{ (cycletype === 'Forecast' ? forecastLabel : actualLabel) + ' Vs ' + (cycletype === 'Forecast' ? 'Previous ' + forecastLabel : forecastLabel) }}
        </div>

        <div class="accordion-section" ref="local_forecast_section" :class="{ 'accordion-disabled': isLocalCommentaryDisabled }">
          <div class="accordion-header d-flex align-center pa-2" :class="{ 'cursor-pointer': !isLocalCommentaryDisabled, 'cursor-not-allowed': isLocalCommentaryDisabled }" @click="!isLocalCommentaryDisabled && (localCommentaryForecastExpanded = !localCommentaryForecastExpanded)" data-testid="commentary-main-tab__local-commentary-forecast-header">
            <span class="font-weight-semibold">{{ commentary_local_key_label }} Commentary (Vs {{ forecastLabelSuffix }})</span>
            <v-spacer></v-spacer>
            <div
                class="d-flex align-center mr-2"
                v-if="
                  $store.state.aiEnabled &&
                  localCommentaryGenAiBtntab &&
                  (selectedcommentary.__myaction === 'localadd' ||
                    selectedcommentary.__myaction === 'regionaladd' ||
                    selectedcommentary.__myaction === 'localvoluntary' ||
                    selectedcommentary.__myaction === 'regionalvoluntary' ||
                    (ispreparer && bulksubmit))
                "
                @click.stop
              >
                <v-btn
                  color="primary"
                  :disabled="selectedcommentary.__stage !== 0 || isTyping || loadingtext1 || loadingtext2 || isThresholdExceedLocalCommentary"
                  :loading="loadingtext2"
                  @click.stop="handleGenerateAi('Two', 'forecast')"
                  data-testid="commentary-main-tab__gen-ai-forecast"
                >
                  <v-icon dark left small>mdi-creation</v-icon>
                  <span class="noTransform">Gen AI</span>
                </v-btn>
                <v-tooltip right content-class="tooltip-right" v-if="isThresholdExceedLocalCommentary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="ml-2">mdi-help-circle-outline</v-icon>
                  </template>
                  <div class="text-center">Hour limit exceeded : Please contact your administrator to increase the limit.</div>
                </v-tooltip>
             </div>
            <v-icon class="mr-2" size="20">{{ localCommentaryForecastExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-show="localCommentaryForecastExpanded" class="accordion-content pa-3">
              <div
                v-if="showLocalForecastSave"
                class="commentary-save-card position-relative"
                data-testid="commentary-main-tab__local-forecast-save-card"
              >
                <lb-textarea
                  :class="[{'shake-error error-highlight': shakingFields['local_forecast']}]"
                  :hidedetails="false"
                  v-model="selectedcommentary.commentary2"
                  :error-messages="persistentErrors['local_forecast']"
                  :label="
                    selectedcommentary.var2_cross_management || selectedcommentary.var2_cross_control || bulksubmit
                      ? commentary_local_key_label + ' Commentary Mandatory'
                      : commentary_local_key_label + ' Commentary Optional'
                  "
                  :required="selectedcommentary.var2_cross_management || selectedcommentary.var2_cross_control || bulksubmit"
                  :disabled="workflowtext.includes('Local control forecast') && workflowtext.includes('Local management forecast')"
                  data-testid="commentary-main-tab__local-commentary-forecast"
                />
              
                <v-btn
                  icon
                  small
                  class="commentary-save-btn"
                  :disabled="!localCommentary2CanSave"
                  :loading="saveLocalCommentaryLoading && saveLocalCommentaryField === 'forecast'"
                  data-testid="commentary-main-tab__local-forecast-save-btn"
                  @click="$emit('save-local-commentary', 'forecast')"
                >
                  <v-icon :color="localCommentary2CanSave ? 'primary' : 'grey lighten-1'">mdi-content-save</v-icon>
                </v-btn>
              </div>
              <lb-textarea
                v-else-if="((bulksubmit && bulktype === 'addlocal') || selectedcommentary.__myaction === 'localadd' || selectedcommentary.__myaction === 'localvoluntary') && selectedcommentary.__stage !== 9"
                :class="[{'shake-error error-highlight': shakingFields['local_forecast']}]"
                :hidedetails="false"
                v-model="selectedcommentary.commentary2"
                :error-messages="persistentErrors['local_forecast']"
                :label="
                  selectedcommentary.var2_cross_management || selectedcommentary.var2_cross_control || bulksubmit
                    ? commentary_local_key_label + ' Commentary Mandatory'
                    : commentary_local_key_label + ' Commentary Optional'
                "
                :required="selectedcommentary.var2_cross_management || selectedcommentary.var2_cross_control || bulksubmit"
                :disabled="workflowtext.includes('Local control forecast') && workflowtext.includes('Local management forecast')"
                data-testid="commentary-main-tab__local-commentary-forecast"
              />
              <div v-else-if="!bulksubmit">
                <template v-if="isForecastJVCommentaryValidForLocal">
                  <div class="d-flex gap-1 mb-1">
                    <v-chip small density="comfortable">JV:</v-chip>
                    <div class="commentary-text">{{ displayJvCommentary(selectedcommentary.jv_commentary2) }}</div>
                  </div>
                </template>
                <div v-else-if="selectedcommentary.commentary2 || selectedcommentary.savedcommentary2" class="commentary-text">{{ selectedcommentary.commentary2 || selectedcommentary.savedcommentary2 }}</div>
                <div class="grey--text" v-else>No Data</div>
              </div>

            </div>
          </v-expand-transition>
        </div>
      </div>
    </div>
    </div>

    <!-- <div
      class="d-flex align-center"
      v-if="(selectedcommentary.upload || (ispreparer && cyclestage === 0)) && !bulksubmit && (workflows.lcp || workflows.lcf || workflows.lmp || workflows.lmf)"
    >
      <div class="mr-2" v-if="selectedcommentary.upload || !commentarySubmitDialogueViewMode">File Attachment</div>
      <lb-file
        v-if="selectedcommentary.upload || !commentarySubmitDialogueViewMode"
        v-model="attachmentfileModel"
        label=""
        :drag="false"
        hidedetails
        @change="handleAttachmentChange"
        :loading="attachmentfileloading"
        :percentage="attachmentfilepercentage"
        accept=".xlsx,.xls,.xlsb,.csv,.pdf,.doc,.docx,.jpg,.jpeg,.png,.ppt,.pptx"
        data-testid="commentary-main-tab__attachment-upload"
      ></lb-file>
      <div class="ml-2" v-if="selectedcommentary.upload">
        <v-btn
          text
          class="shadow-off secondaryBtn"
          @click="$nova.downloadFile(undefined, {}, '/v2/varianceanalysis/analysis/downloadattachment/' + selectedcommentary._id + '/t/' + cycletype, '&uploadid=' + selectedcommentary.upload + '&timestamp=' + new Date().getTime())"
          data-testid="commentary-main-tab__attachment-download"
        >
          <v-icon class="mr-1">mdi-download</v-icon>
          <span>Download</span>
        </v-btn>
      </div>
    </div> -->

    <div v-if="!bulksubmit && selectedcommentary.__myaction === 'localvoluntary'">
      <div>{{ commentary_local_key_label + ' Voluntary commentary submission reason' }}</div>
      <v-checkbox
        hide-details
        class="pa-0 ma-0"
        dense
        v-model="requestcommentarylocalcontrolvsProformaModel"
        :label="'For the purpose of controls vs ' + proformaLabel + '?'"
        :disabled="workflowtext.includes('Local control proforma')"
        data-testid="commentary-main-tab__request-local-control-proforma"
      ></v-checkbox>
      <v-checkbox
        hide-details
        class="pa-0 ma-0"
        dense
        v-model="requestcommentarylocalcontrolvsForecastModel"
        :label="'For the purpose of controls vs ' + forecastLabel + '?'"
        :disabled="workflowtext.includes('Local control forecast')"
        data-testid="commentary-main-tab__request-local-control-forecast"
      ></v-checkbox>
      <v-checkbox
        hide-details
        class="pa-0 ma-0"
        dense
        v-model="requestcommentarylocalmanagementvsProformaModel"
        :label="'For the purpose of management vs ' + proformaLabel + '?'"
        :disabled="workflowtext.includes('Local management proforma')"
        data-testid="commentary-main-tab__request-local-management-proforma"
      ></v-checkbox>
      <v-checkbox
        hide-details
        class="pa-0 ma-0"
        dense
        v-model="requestcommentarylocalmanagementvsForecastModel"
        :label="'For the purpose of management vs ' + forecastLabel + '?'"
        :disabled="workflowtext.includes('Local management forecast')"
        data-testid="commentary-main-tab__request-local-management-forecast"
      ></v-checkbox>
    </div>

    <!-- Local Approval Section - Hidden for regional approver bulk -->
    <div v-if="!isRegionalApproverBulk" :class="{ 'section-disabled': isLocalApprovalDisabled }">
      <div class="mb-2" v-if="selectedcommentary.__myaction === 'localapprove' && bulksubmit">
        <span class="font-weight-semibold mr-1">Note:</span>
        Bulk approval/rejection cannot be done for GL line items whose variances breach the thresholds set for the purpose of controls.
      </div>
      <div class="accordion-section" ref="local_approval_reason_section" :class="{ 'accordion-disabled': isLocalApprovalDisabled }">
        <div class="accordion-header d-flex align-center pa-2" :class="{ 'cursor-pointer': !isLocalApprovalDisabled, 'cursor-not-allowed': isLocalApprovalDisabled }" @click="!isLocalApprovalDisabled && (localApprovalReasonExpanded = !localApprovalReasonExpanded)" data-testid="commentary-main-tab__local-approval-reason-header">
          <span class="font-weight-semibold">{{ commentary_local_key_label }} Approval/Rejection Reason</span>
          <v-spacer></v-spacer>
          <v-icon class="mr-2" size="20">{{ localApprovalReasonExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-show="localApprovalReasonExpanded" class="accordion-content pa-3">
            <div
              v-if="showLocalReasonSave"
              class="commentary-save-card position-relative"
              data-testid="commentary-main-tab__local-reason-save-card"
            >
              <lb-textarea
                :class="[{'shake-error error-highlight': shakingFields['local_approval_reason']}]"
                :hidedetails="false"
                v-model="selectedcommentary.approvereason"
                :error-messages="persistentErrors['local_approval_reason']"
                :label="`${$nova.labelsName('cycle_page','commentary_local_approver_label','Local Approver')}'s Approval/Reject Reason`"
                data-testid="commentary-main-tab__local-approval-reason"
              />
              <v-btn
                icon
                small
                class="commentary-save-btn"
                :disabled="!canSaveLocalReason"
                :loading="saveLocalReasonLoading"
                data-testid="commentary-main-tab__local-reason-save-btn"
                @click="$emit('save-local-reason')"
              >
                <v-icon :color="canSaveLocalReason ? 'primary' : 'grey lighten-1'">mdi-content-save</v-icon>
              </v-btn>
            </div>
            <lb-textarea
              v-else-if="(bulksubmit && bulktype === 'approvelocal') || selectedcommentary.__myaction === 'localapprove'"
              :class="[{'shake-error error-highlight': shakingFields['local_approval_reason']}]"
              :hidedetails="false"
              v-model="selectedcommentary.approvereason"
              :error-messages="persistentErrors['local_approval_reason']"
              :label="`${$nova.labelsName('cycle_page','commentary_local_approver_label','Local Approver')}'s Approval/Reject Reason`"
              data-testid="commentary-main-tab__local-approval-reason"
            />
            <div v-else-if="!bulksubmit">
              <div v-if="selectedcommentary.rejectreason">
                <div class="commentary-text">{{ selectedcommentary.rejectreason }}</div>
              </div>
              <div v-else-if="selectedcommentary.approvereason">
                <div class="commentary-text">{{ selectedcommentary.approvereason }}</div>
              </div>
              <div v-else>
                <div class="grey--text">No Data</div>
              </div>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </div>

    <div v-if="!bulksubmit && selectedcommentary.__myaction === 'regionalvoluntary'">
      <div>{{ commentary_regional_key_label }} Voluntary commentary submission reason</div>
      <v-checkbox
        hide-details
        class="pa-0 ma-0"
        dense
        v-model="requestcommentaryregionalcontrolModel"
        label="For the purpose of controls?"
        data-testid="commentary-main-tab__request-regional-control-voluntary"
      ></v-checkbox>
      <v-checkbox
        hide-details
        class="pa-0 ma-0"
        dense
        v-model="requestcommentaryregionalmanagementModel"
        label="For the purpose of management?"
        data-testid="commentary-main-tab__request-regional-management-voluntary"
      ></v-checkbox>
    </div>

    <!-- Regional Commentary Sections - Hidden during bulk approve -->
    <div v-if="!isBulkApprove" class="d-flex gap-4" :class="{ 'section-disabled': isRegionalSectionsDisabled }">
      <div style="width:50%">
        <div v-if="bulksubmit && bulktype === 'addregional'" class="font-size-md pl-2 border-left-custom noTransform mb-2" :style="{ '--custom-border-color': tablecolors.heading.a }">
          {{ (cycletype === 'Forecast' ? forecastLabel : actualLabel) + ' Vs ' + proformaLabel }}
        </div>
        <div class="accordion-section" ref="regional_proforma_section" :class="{ 'accordion-disabled': isRegionalSectionsDisabled }">
          <div class="accordion-header d-flex align-center pa-2" :class="{ 'cursor-pointer': !isRegionalSectionsDisabled, 'cursor-not-allowed': isRegionalSectionsDisabled }" @click="!isRegionalSectionsDisabled && (regionalCommentaryProformaExpanded = !regionalCommentaryProformaExpanded)" data-testid="commentary-main-tab__regional-commentary-proforma-header">
            <span class="font-weight-semibold">{{ commentary_regional_key_label }} Commentary (Vs {{ proformaLabel }})</span>
            <v-spacer></v-spacer>
            <v-icon class="mr-2" size="20">{{ regionalCommentaryProformaExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-show="regionalCommentaryProformaExpanded" class="accordion-content pa-3">
              <div
                v-if="showRegionalProformaSave"
                class="commentary-save-card position-relative"
                data-testid="commentary-main-tab__regional-proforma-save-card"
              >
                <lb-textarea
                  :class="[{'shake-error error-highlight': shakingFields['regional_proforma']}]"
                  :hidedetails="false"
                  v-model="selectedcommentary.regionalcommentary1"
                  :error-messages="persistentErrors['regional_proforma']"
                  :label="
                    selectedcommentary.var1_cross_management_regional || selectedcommentary.var1_cross_control_regional || bulksubmit
                      ? commentary_regional_key_label + ' Commentary Mandatory'
                      : commentary_regional_key_label + ' Commentary Optional'
                  "
                  :required="selectedcommentary.var1_cross_management_regional || selectedcommentary.var1_cross_control_regional || bulksubmit"
                  :disabled="workflowtext.includes('Regional control proforma') && workflowtext.includes('Regional management proforma')"
                  data-testid="commentary-main-tab__regional-commentary-proforma"
                />
               
                <v-btn
                  icon
                  small
                  class="commentary-save-btn"
                  :disabled="!regionalCommentary1CanSave"
                  :loading="saveRegionalCommentaryLoading && saveRegionalCommentaryField === 'proforma'"
                  data-testid="commentary-main-tab__regional-proforma-save-btn"
                  @click="$emit('save-regional-commentary', 'proforma')"
                >
                  <v-icon :color="regionalCommentary1CanSave ? 'primary' : 'grey lighten-1'">mdi-content-save</v-icon>
                </v-btn>
              </div>
              <lb-textarea
                v-else-if="isRegionalCommentaryEditable"
                :class="[{'shake-error error-highlight': shakingFields['regional_proforma']}]"
                :hidedetails="false"
                v-model="selectedcommentary.regionalcommentary1"
                :error-messages="persistentErrors['regional_proforma']"
                :label="
                  selectedcommentary.var1_cross_management_regional || selectedcommentary.var1_cross_control_regional || bulksubmit
                    ? commentary_regional_key_label + ' Commentary Mandatory'
                    : commentary_regional_key_label + ' Commentary Optional'
                "
                :required="selectedcommentary.var1_cross_management_regional || selectedcommentary.var1_cross_control_regional || bulksubmit"
                :disabled="workflowtext.includes('Regional control proforma') && workflowtext.includes('Regional management proforma')"
                data-testid="commentary-main-tab__regional-commentary-proforma"
              />
              <div v-else-if="!bulksubmit">
                <template v-if="isActualJVCommentaryValidForRegional">
                  <div class="d-flex gap-1 mb-1">
                    <v-chip small density="comfortable">JV:</v-chip>
                    <div class="commentary-text">{{ displayJvCommentary(selectedcommentary.jv_commentary1) }}</div>
                  </div>
                </template>
                <div v-else-if="selectedcommentary.regionalcommentary1 || selectedcommentary.savedregionalcommentary1" class="commentary-text">{{ selectedcommentary.regionalcommentary1 || selectedcommentary.savedregionalcommentary1 }}</div>
                <div class="grey--text" v-else>No Data</div>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </div>
      <div style="width:50%">
        <div v-if="bulksubmit && bulktype === 'addregional'" class="font-size-md pl-2 noTransform mb-2" :style="`border-left: 4px solid ${tablecolors.heading.b} !important`">
          {{ (cycletype === 'Forecast' ? forecastLabel : actualLabel) + ' Vs ' + (cycletype === 'Forecast' ? 'Previous ' + forecastLabel : forecastLabel) }}
        </div>
        <div class="accordion-section" ref="regional_forecast_section" :class="{ 'accordion-disabled': isRegionalSectionsDisabled }">
          <div class="accordion-header d-flex align-center pa-2" :class="{ 'cursor-pointer': !isRegionalSectionsDisabled, 'cursor-not-allowed': isRegionalSectionsDisabled }" @click="!isRegionalSectionsDisabled && (regionalCommentaryForecastExpanded = !regionalCommentaryForecastExpanded)" data-testid="commentary-main-tab__regional-commentary-forecast-header">
            <span class="font-weight-semibold">{{ commentary_regional_key_label }} Commentary (Vs {{ forecastLabelSuffix }})</span>
            <v-spacer></v-spacer>
            <v-icon class="mr-2" size="20">{{ regionalCommentaryForecastExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <v-expand-transition>
            <div v-show="regionalCommentaryForecastExpanded" class="accordion-content pa-3">
              <div
                v-if="showRegionalForecastSave"
                class="commentary-save-card position-relative"
                data-testid="commentary-main-tab__regional-forecast-save-card"
              >
                <lb-textarea
                  :class="[{'shake-error error-highlight': shakingFields['regional_forecast']}]"
                  :hidedetails="false"
                  v-model="selectedcommentary.regionalcommentary2"
                  :error-messages="persistentErrors['regional_forecast']"
                  :label="
                    selectedcommentary.var2_cross_management_regional || selectedcommentary.var2_cross_control_regional || bulksubmit
                      ? commentary_regional_key_label + ' Commentary Mandatory'
                      : commentary_regional_key_label + ' Commentary Optional'
                  "
                  :required="selectedcommentary.var2_cross_management_regional || selectedcommentary.var2_cross_control_regional || bulksubmit"
                  :disabled="workflowtext.includes('Regional control forecast') && workflowtext.includes('Regional management forecast')"
                  data-testid="commentary-main-tab__regional-commentary-forecast"
                />
                
                <v-btn
                  icon
                  small
                  class="commentary-save-btn"
                  :disabled="!regionalCommentary2CanSave"
                  :loading="saveRegionalCommentaryLoading && saveRegionalCommentaryField === 'forecast'"
                  data-testid="commentary-main-tab__regional-forecast-save-btn"
                  @click="$emit('save-regional-commentary', 'forecast')"
                >
                  <v-icon :color="regionalCommentary2CanSave ? 'primary' : 'grey lighten-1'">mdi-content-save</v-icon>
                </v-btn>
              </div>
              <lb-textarea
                v-else-if="isRegionalCommentaryEditable"
                :class="[{'shake-error error-highlight': shakingFields['regional_forecast']}]"
                :hidedetails="false"
                v-model="selectedcommentary.regionalcommentary2"
                :error-messages="persistentErrors['regional_forecast']"
                :label="
                  selectedcommentary.var2_cross_management_regional || selectedcommentary.var2_cross_control_regional || bulksubmit
                    ? commentary_regional_key_label + ' Commentary Mandatory'
                    : commentary_regional_key_label + ' Commentary Optional'
                "
                :required="selectedcommentary.var2_cross_management_regional || selectedcommentary.var2_cross_control_regional || bulksubmit"
                :disabled="workflowtext.includes('Regional control forecast') && workflowtext.includes('Regional management forecast')"
                data-testid="commentary-main-tab__regional-commentary-forecast"
              />
              <div v-else-if="!bulksubmit">
                <template v-if="isForecastJVCommentaryValidForRegional">
                  <div class="d-flex gap-1 mb-1">
                    <v-chip small density="comfortable">JV:</v-chip>
                    <div class="commentary-text">{{ displayJvCommentary(selectedcommentary.jv_commentary2) }}</div>
                  </div>
                </template>
                <div v-else-if="selectedcommentary.regionalcommentary2 || selectedcommentary.savedregionalcommentary2" class="commentary-text">{{ selectedcommentary.regionalcommentary2 || selectedcommentary.savedregionalcommentary2 }}</div>
                <div class="grey--text" v-else>No Data</div>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </div>

    <!-- Regional Approval Section - Hidden for local approver bulk -->
    <div v-if="!isLocalApproverBulk" class="accordion-section" ref="regional_approval_reason_section" :class="{ 'accordion-disabled': isRegionalApprovalDisabled, 'section-disabled': isRegionalApprovalDisabled }">
      <div class="accordion-header d-flex align-center pa-2" :class="{ 'cursor-pointer': !isRegionalApprovalDisabled, 'cursor-not-allowed': isRegionalApprovalDisabled }" @click="!isRegionalApprovalDisabled && (regionalApprovalReasonExpanded = !regionalApprovalReasonExpanded)" data-testid="commentary-main-tab__regional-approval-reason-header">
        <span class="font-weight-semibold">{{ commentary_regional_key_label }} Approval/Rejection Reason</span>
        <v-spacer></v-spacer>
        <v-icon class="mr-2" size="20">{{ regionalApprovalReasonExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </div>
      <v-expand-transition>

        <div v-show="regionalApprovalReasonExpanded" class="accordion-content pa-3">
          <div
            v-if="showRegionalReasonSave"
            class="regional-reason-save-card position-relative"
            data-testid="commentary-main-tab__regional-reason-save-card"
          >
            <lb-textarea
              :class="[{'shake-error error-highlight': shakingFields['regional_approval_reason']}]"
              :hidedetails="false"
              v-model="selectedcommentary.regionalapprovereason"
              :label="`${$nova.labelsName('cycle_page','commentary_regional_approver_label','Regional Approver')}'s Approval/Reject Reason`"
              data-testid="commentary-main-tab__regional-approval-reason"
            />
            <v-btn
              icon
              small
              class="regional-reason-save-btn"
              :disabled="!canSaveRegionalReason"
              :loading="saveRegionalReasonLoading"
              data-testid="commentary-main-tab__regional-reason-save-btn"
              @click="$emit('save-regional-reason')"
            >
              <v-icon :color="canSaveRegionalReason ? 'primary' : 'grey lighten-1'">mdi-content-save</v-icon>
            </v-btn>
          </div>
          <lb-textarea
            v-else-if="(bulksubmit && bulktype === 'approveregional') || (selectedcommentary.__myaction === 'regionalapprove' || (isregionalapprover && selectedcommentary.stage_local === 2 && selectedcommentary.stage_regional === 1)) && commentarySubmitDialogueViewMode !== true"
            :class="[{'shake-error error-highlight': shakingFields['regional_approval_reason']}]"
            :hidedetails="false"
            v-model="selectedcommentary.regionalapprovereason"
            :label="`${$nova.labelsName('cycle_page','commentary_regional_approver_label','Regional Approver')}'s Approval/Reject Reason`"
            data-testid="commentary-main-tab__regional-approval-reason"
          />
          <div v-else-if="!bulksubmit">
            <div v-if="selectedcommentary.regionalrejectreason">
              <div class="commentary-text">{{ selectedcommentary.regionalrejectreason }}</div>
            </div>
            <div v-else-if="selectedcommentary.regionalapprovereason">
              <div class="commentary-text">{{ selectedcommentary.regionalapprovereason }}</div>
            </div>
            <div v-else>
              <div class="grey--text">No Data</div>
            </div>
          </div>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script>
import helpers from '@/modules/varianceanalysis/utils/helpers';

export default {
  name: 'CommentaryMainTab',
  props: {
    contextType: { type: String, default: 'dialog' },
    bulksubmit: { type: Boolean, default: false },
    bulktype: { type: String, default: '' },
    selectedcommentary: { type: Object, default: () => ({}) },
    multiselectdata: { type: Array, default: () => [] },
    cycletype: { type: String, default: '' },
    commentary_local_key_label: { type: String, default: 'Local' },
    commentary_regional_key_label: { type: String, default: 'Regional' },
    workflowtext: { type: Array, default: () => [] },
    workflows: { type: Object, default: () => ({}) },
    tablecolors: {
      type: Object,
      default: () => ({ heading: { a: '', b: '' }, body: { a: '', b: '' } }),
    },
    ispreparer: { type: Boolean, default: false },
    isapprover: { type: Boolean, default: false },
    isregionalapprover: { type: Boolean, default: false },
    cyclestage: { type: Number, default: 0 },
    commentarySubmitDialogueViewMode: { type: Boolean, default: false },
    localCommentaryGenAiBtntab: { type: Boolean, default: false },
    isRegionalCommentaryAutomationEnabled: { type: Boolean, default: false },
    isGlMandatoryRequestFlag: { type: Boolean, default: false },
    replaceExistingCommentFlag: { type: Boolean, default: false },
    lastActionsOnGL: { type: Object, default: () => ({}) },
    isTyping: { type: Boolean, default: false },
    loadingtext1: { type: Boolean, default: false },
    loadingtext2: { type: Boolean, default: false },
    isThresholdExceedLocalCommentary: { type: Boolean, default: false },
    bulkcommentall: { type: Boolean, default: false },
    bulkcommentreplace: { type: Boolean, default: false },
    requestcommentarylocalcontrol: { type: Boolean, default: false },
    requestcommentarylocalmanagement: { type: Boolean, default: false },
    requestcommentaryregionalcontrol: { type: Boolean, default: false },
    requestcommentaryregionalmanagement: { type: Boolean, default: false },
    requestcommentarylocalcontrolvsProforma: { type: Boolean, default: false },
    requestcommentarylocalcontrolvsForecast: { type: Boolean, default: false },
    requestcommentarylocalmanagementvsProforma: { type: Boolean, default: false },
    requestcommentarylocalmanagementvsForecast: { type: Boolean, default: false },
    requestcommentaryregionalcontrolvsProforma: { type: Boolean, default: false },
    requestcommentaryregionalmanagementvsProforma: { type: Boolean, default: false },
    requestcommentaryregionalcontrolvsForecast: { type: Boolean, default: false },
    requestcommentaryregionalmanagementvsForecast: { type: Boolean, default: false },
    attachmentfile: { type: [File, Object, String, null], default: null },
    attachmentfileloading: { type: Boolean, default: false },
    attachmentfilepercentage: { type: Number, default: 0 },
    isLocalCommentaryCollapsed: { type: Boolean, default: false },
    isLocalCommentaryReasonCollapsed: { type: Boolean, default: false },
    validationErrorField: { type: String, default: '' },
    hideSummarySection: { type: Boolean, default: false },
    actualLabel: { type: String, default: 'Actual' },
    proformaLabel: { type: String, default: 'Proforma' },
    forecastLabel: { type: String, default: 'Forecast' },
    saveRegionalReasonLoading: { type: Boolean, default: false },
    saveLocalReasonLoading: { type: Boolean, default: false },
    saveLocalCommentaryLoading: { type: Boolean, default: false },
    saveLocalCommentaryField: { type: String, default: '' },
    saveRegionalCommentaryLoading: { type: Boolean, default: false },
    saveRegionalCommentaryField: { type: String, default: '' },
    commentaryBaselines: { type: Object, default: () => ({}) },
    commentaryBaselinesReady: { type: Boolean, default: false },
  },
  emits: [
    'update:bulkcommentall',
    'update:bulkcommentreplace',
    'update:requestcommentarylocalcontrol',
    'update:requestcommentarylocalmanagement',
    'update:requestcommentaryregionalcontrol',
    'update:requestcommentaryregionalmanagement',
    'update:requestcommentarylocalcontrolvsProforma',
    'update:requestcommentarylocalcontrolvsForecast',
    'update:requestcommentarylocalmanagementvsProforma',
    'update:requestcommentarylocalmanagementvsForecast',
    'update:requestcommentaryregionalcontrolvsProforma',
    'update:requestcommentaryregionalmanagementvsProforma',
    'update:requestcommentaryregionalcontrolvsForecast',
    'update:requestcommentaryregionalmanagementvsForecast',
    'update:attachmentfile',
    'toggle-local-commentary-collapse',
    'toggle-local-commentary-reason-collapse',
    'generate-ai',
    'attachment-change',
    'save-regional-reason',
    'save-local-reason',
    'save-local-commentary',
    'save-regional-commentary',
    'commentary-display-ready',
  ],
  data() {
    return {
      localCommentaryProformaExpanded: !this.isLocalCommentaryCollapsed,
      localCommentaryForecastExpanded: !this.isLocalCommentaryCollapsed,
      localApprovalReasonExpanded: !this.isLocalCommentaryReasonCollapsed,
      regionalCommentaryProformaExpanded: true,
      regionalCommentaryForecastExpanded: true,
      regionalApprovalReasonExpanded: true,
      shakingFields: {},
      persistentErrors: {},
      displayBaselines: {},
      displayBaselinesReady: false,
      commentaryFieldDirty: {
        commentary1: false,
        commentary2: false,
        approvereason: false,
        regionalcommentary1: false,
        regionalcommentary2: false,
        regionalapprovereason: false,
      },
      localCommentary1Saved: false,
      localCommentary2Saved: false,
      localCommentary1CanSave: false,
      localCommentary2CanSave: false,
      regionalCommentary1Saved: false,
      regionalCommentary2Saved: false,
      regionalCommentary1CanSave: false,
      regionalCommentary2CanSave: false,
      displayBaselinesCommentaryId: '',
    };
  },
  created() {
    this.initializeSectionStates();
    this.ensureSubmittedCommentarySnapshots();
    this.prefillJVCommentary();
    this.prefillRegionalCommentary();
    this.scheduleDisplayBaselineCapture();
  },
  computed: {
    glCodeName() {
      return (this.selectedcommentary && this.selectedcommentary.glcode_detail && this.selectedcommentary.glcode_detail.name) || '';
    },
    bulkcommentallModel: {
      get() {
        return this.bulkcommentall;
      },
      set(val) {
        this.$emit('update:bulkcommentall', val);
      },
    },
    bulkcommentreplaceModel: {
      get() {
        return this.bulkcommentreplace;
      },
      set(val) {
        this.$emit('update:bulkcommentreplace', val);
      },
    },
    requestcommentarylocalcontrolModel: {
      get() {
        return this.requestcommentarylocalcontrol;
      },
      set(val) {
        this.$emit('update:requestcommentarylocalcontrol', val);
      },
    },
    requestcommentarylocalmanagementModel: {
      get() {
        return this.requestcommentarylocalmanagement;
      },
      set(val) {
        this.$emit('update:requestcommentarylocalmanagement', val);
      },
    },
    requestcommentaryregionalcontrolModel: {
      get() {
        return this.requestcommentaryregionalcontrol;
      },
      set(val) {
        this.$emit('update:requestcommentaryregionalcontrol', val);
      },
    },
    requestcommentaryregionalmanagementModel: {
      get() {
        return this.requestcommentaryregionalmanagement;
      },
      set(val) {
        this.$emit('update:requestcommentaryregionalmanagement', val);
      },
    },
    requestcommentarylocalcontrolvsProformaModel: {
      get() {
        return this.requestcommentarylocalcontrolvsProforma;
      },
      set(val) {
        this.$emit('update:requestcommentarylocalcontrolvsProforma', val);
      },
    },
    requestcommentarylocalcontrolvsForecastModel: {
      get() {
        return this.requestcommentarylocalcontrolvsForecast;
      },
      set(val) {
        this.$emit('update:requestcommentarylocalcontrolvsForecast', val);
      },
    },
    requestcommentarylocalmanagementvsProformaModel: {
      get() {
        return this.requestcommentarylocalmanagementvsProforma;
      },
      set(val) {
        this.$emit('update:requestcommentarylocalmanagementvsProforma', val);
      },
    },
    requestcommentarylocalmanagementvsForecastModel: {
      get() {
        return this.requestcommentarylocalmanagementvsForecast;
      },
      set(val) {
        this.$emit('update:requestcommentarylocalmanagementvsForecast', val);
      },
    },
    requestcommentaryregionalcontrolvsProformaModel: {
      get() {
        return this.requestcommentaryregionalcontrolvsProforma;
      },
      set(val) {
        this.$emit('update:requestcommentaryregionalcontrolvsProforma', val);
      },
    },
    requestcommentaryregionalmanagementvsProformaModel: {
      get() {
        return this.requestcommentaryregionalmanagementvsProforma;
      },
      set(val) {
        this.$emit('update:requestcommentaryregionalmanagementvsProforma', val);
      },
    },
    requestcommentaryregionalcontrolvsForecastModel: {
      get() {
        return this.requestcommentaryregionalcontrolvsForecast;
      },
      set(val) {
        this.$emit('update:requestcommentaryregionalcontrolvsForecast', val);
      },
    },
    requestcommentaryregionalmanagementvsForecastModel: {
      get() {
        return this.requestcommentaryregionalmanagementvsForecast;
      },
      set(val) {
        this.$emit('update:requestcommentaryregionalmanagementvsForecast', val);
      },
    },
    attachmentfileModel: {
      get() {
        return this.attachmentfile;
      },
      set(val) {
        this.$emit('update:attachmentfile', val);
      },
    },
    forecastLabelSuffix() {
      return this.cycletype === 'Forecast'
        ? `Previous ${this.forecastLabel}`
        : this.forecastLabel;
    },
    /**
     * Check if user is local preparer doing bulk commentary
     * Used to collapse and grey out non-relevant sections
     */
    isLocalPreparerBulk() {
      return this.bulksubmit && this.ispreparer && this.bulktype === 'addlocal';
    },
    /**
     * Check if user is local approver doing bulk approval
     * Used to collapse and grey out non-relevant sections
     */
    isLocalApproverBulk() {
      return this.bulksubmit && this.isapprover && this.bulktype === 'approvelocal';
    },
    /**
     * Check if user is regional approver doing bulk approval
     * Used to collapse and grey out non-relevant sections
     */
    isRegionalApproverBulk() {
      return this.bulksubmit && this.isregionalapprover && this.bulktype === 'approveregional';
    },
    /**
     * Check if this is a bulk approve operation (local or regional)
     * Used to hide preparer commentary sections
     */
    isBulkApprove() {
      return this.bulksubmit && ['approvelocal', 'approveregional'].includes(this.bulktype);
    },
    /**
     * Check if local commentary sections should be disabled
     * Disabled for local approver bulk (they only need approval reason)
     */
    isLocalCommentaryDisabled() {
      return this.isLocalApproverBulk;
    },
    /**
     * Check if local approval section should be disabled
     * Disabled for local preparer bulk (they only need commentary sections)
     */
    isLocalApprovalDisabled() {
      return this.isLocalPreparerBulk;
    },
    /**
     * Check if regional sections should be disabled
     * Disabled for both local preparer and local approver bulk operations
     */
    isRegionalSectionsDisabled() {
      return this.isLocalPreparerBulk || this.isLocalApproverBulk;
    },
    /**
     * Check if regional approval section should be disabled
     * Should NOT be disabled for regional approver bulk (they need to enter approval reason)
     */
    isRegionalApprovalDisabled() {
      return (this.isLocalPreparerBulk || this.isLocalApproverBulk) && !this.isRegionalApproverBulk;
    },
    showRegionalReasonSave() {
      return (
        this.isregionalapprover &&
        !this.bulksubmit &&
        !this.commentarySubmitDialogueViewMode &&
        (this.selectedcommentary.__myaction === 'regionalapprove' ||
          (this.selectedcommentary.stage_local === 2 && this.selectedcommentary.stage_regional === 1))
      );
    },
    canSaveRegionalReason() {
      return this.isCommentaryFieldDirty(
        this.selectedcommentary?.regionalapprovereason,
        'regionalapprovereason'
      );
    },
    showLocalReasonSave() {
      return (
        this.isapprover &&
        !this.isregionalapprover &&
        !this.bulksubmit &&
        this.selectedcommentary.__myaction === 'localapprove'
      );
    },
    canSaveLocalReason() {
      return this.isCommentaryFieldDirty(
        this.selectedcommentary?.approvereason,
        'approvereason'
      );
    },
    showLocalCommentarySave() {
      return (
        this.ispreparer &&
        !this.bulksubmit &&
        (this.contextType === 'glbreakdown' || this.contextType === 'dialog') &&
        (this.selectedcommentary.__myaction === 'localadd' ||
          this.selectedcommentary.__myaction === 'localvoluntary') &&
        this.selectedcommentary.__stage !== 9
      );
    },
    showLocalProformaSave() {
      return this.showLocalCommentarySave && (this.workflows.lcp || this.workflows.lmp);
    },
    showLocalForecastSave() {
      return this.showLocalCommentarySave && (this.workflows.lcf || this.workflows.lmf);
    },
    showLocalCommentaryFieldSaveState() {
      return this.contextType === 'dialog' && this.showLocalCommentarySave;
    },
    showLocalCommentary1SaveState() {
      if (!this.showLocalCommentaryFieldSaveState) {
        return false;
      }
      if (this.isLocalCommentaryFieldSubmitted('commentary1')) {
        return true;
      }
      const saved = this.normalizeCommentaryText(this.selectedcommentary?.savedcommentary1);
      const current = this.normalizeCommentaryText(this.selectedcommentary?.commentary1);
      return Boolean(saved) || Boolean(current);
    },
    showLocalCommentary2SaveState() {
      if (!this.showLocalCommentaryFieldSaveState) {
        return false;
      }
      if (this.isLocalCommentaryFieldSubmitted('commentary2')) {
        return true;
      }
      const saved = this.normalizeCommentaryText(this.selectedcommentary?.savedcommentary2);
      const current = this.normalizeCommentaryText(this.selectedcommentary?.commentary2);
      return Boolean(saved) || Boolean(current);
    },
    showRegionalCommentarySave() {
      return (
        this.isRegionalPreparer &&
        !this.bulksubmit &&
        (this.contextType === 'glbreakdown' || this.contextType === 'dialog') &&
        (this.selectedcommentary.__myaction === 'regionaladd' ||
          this.selectedcommentary.__myaction === 'regionalvoluntary') &&
        this.selectedcommentary.__stage !== 9
      );
    },
    showRegionalProformaSave() {
      return this.showRegionalCommentarySave && (this.workflows.rcp || this.workflows.rmp);
    },
    showRegionalForecastSave() {
      return this.showRegionalCommentarySave && (this.workflows.rcf || this.workflows.rmf);
    },
    showRegionalCommentaryFieldSaveState() {
      return this.contextType === 'dialog' && this.showRegionalCommentarySave;
    },
    showRegionalCommentary1SaveState() {
      if (!this.showRegionalCommentaryFieldSaveState) {
        return false;
      }
      if (this.isRegionalCommentaryFieldSubmitted('regionalcommentary1')) {
        return true;
      }
      const saved = this.normalizeCommentaryText(this.selectedcommentary?.savedregionalcommentary1);
      const current = this.normalizeCommentaryText(this.selectedcommentary?.regionalcommentary1);
      return Boolean(saved) || Boolean(current);
    },
    showRegionalCommentary2SaveState() {
      if (!this.showRegionalCommentaryFieldSaveState) {
        return false;
      }
      if (this.isRegionalCommentaryFieldSubmitted('regionalcommentary2')) {
        return true;
      }
      const saved = this.normalizeCommentaryText(this.selectedcommentary?.savedregionalcommentary2);
      const current = this.normalizeCommentaryText(this.selectedcommentary?.regionalcommentary2);
      return Boolean(saved) || Boolean(current);
    },
    isRegionalPreparer() {
      // Local approver acts as regional preparer during regional submission stages.
      return this.isapprover && !this.isregionalapprover;
    },
    isRegionalCommentaryEditable() {
      if (this.bulksubmit) {
        return this.bulktype === 'addregional' && this.isRegionalPreparer;
      }
      return (
        this.isRegionalPreparer &&
        (this.selectedcommentary.__myaction === 'regionaladd' ||
          this.selectedcommentary.__myaction === 'regionalvoluntary') &&
        this.selectedcommentary.__stage !== 9
      );
    },
    getLocalLastAction() {
      return (
        (this.lastActionsOnGL && this.lastActionsOnGL.localaction && this.lastActionsOnGL.localaction.lastAction) ||
        ''
      );
    },
    getRegionalLastAction() {
      return (
        (this.lastActionsOnGL && this.lastActionsOnGL.regionalaction && this.lastActionsOnGL.regionalaction.lastAction) ||
        ''
      );
    },
    isActualJVCommentaryValidForLocal() {
      return helpers.isActualJVCommentaryValidForLocal.call(helpers, this.selectedcommentary, this.workflows);
    },
    isForecastJVCommentaryValidForLocal() {
      return helpers.isForecastJVCommentaryValidForLocal.call(helpers, this.selectedcommentary, this.workflows);
    },
    isActualJVCommentaryValidForRegional() {
      return helpers.isActualJVCommentaryValidForRegional.call(helpers, this.selectedcommentary, this.workflows);
    },
    isForecastJVCommentaryValidForRegional() {
      return helpers.isForecastJVCommentaryValidForRegional.call(helpers, this.selectedcommentary, this.workflows);
    },
  },
  watch: {
    selectedcommentary: {
      immediate: false,
      deep: false,
      handler() {
        this.ensureSubmittedCommentarySnapshots();
        this.prefillJVCommentary();
        this.prefillRegionalCommentary();
        this.scheduleDisplayBaselineCapture();
      },
    },
    commentaryBaselines: {
      deep: true,
      handler(newVal) {
        if (!this.commentaryBaselinesReady || !newVal) {
          return;
        }
        Object.keys(this.commentaryFieldDirty).forEach((field) => {
          if (!Object.prototype.hasOwnProperty.call(newVal, field)) {
            return;
          }
          this.$set(
            this.displayBaselines,
            field,
            this.normalizeCommentaryText(newVal[field])
          );
        });
        this.displayBaselinesReady = true;
        this.refreshCommentaryDirtyState();
        this.refreshLocalCommentarySaveStates();
        this.refreshRegionalCommentarySaveStates();
      },
    },
    commentaryBaselinesReady(val) {
      if (val) {
        return;
      }
      this.refreshCommentaryDirtyState();
    },
    'selectedcommentary.commentary1'(val) {
      this.refreshCommentaryDirtyState();
      this.refreshLocalCommentarySaveStates();
      if (!val || val.trim().length === 0 || val.trim().length >= 15) {
        this.$set(this.persistentErrors, 'local_proforma', null);
      }
    },
    'selectedcommentary.commentary2'(val) {
      this.refreshCommentaryDirtyState();
      this.refreshLocalCommentarySaveStates();
      if (!val || val.trim().length === 0 || val.trim().length >= 15) {
        this.$set(this.persistentErrors, 'local_forecast', null);
      }
    },
    'selectedcommentary.savedcommentary1'() {
      this.refreshLocalCommentarySaveStates();
    },
    'selectedcommentary.savedcommentary2'() {
      this.refreshLocalCommentarySaveStates();
    },
    'selectedcommentary.approvereason'(val) {
      this.refreshCommentaryDirtyState();
      if (val && val.trim().length > 0) {
        this.$set(this.persistentErrors, 'local_approval_reason', null);
      }
    },
    'selectedcommentary.regionalcommentary1'(val) {
      this.refreshCommentaryDirtyState();
      this.refreshRegionalCommentarySaveStates();
      if (!val || val.trim().length === 0 || val.trim().length >= 15) {
        this.$set(this.persistentErrors, 'regional_proforma', null);
      }
    },
    'selectedcommentary.regionalcommentary2'(val) {
      this.refreshCommentaryDirtyState();
      this.refreshRegionalCommentarySaveStates();
      if (!val || val.trim().length === 0 || val.trim().length >= 15) {
        this.$set(this.persistentErrors, 'regional_forecast', null);
      }
    },
    'selectedcommentary.savedregionalcommentary1'() {
      this.refreshRegionalCommentarySaveStates();
    },
    'selectedcommentary.savedregionalcommentary2'() {
      this.refreshRegionalCommentarySaveStates();
    },
    'selectedcommentary.regionalapprovereason'() {
      this.refreshCommentaryDirtyState();
    },
    isLocalCommentaryCollapsed(val) {
      this.localCommentaryProformaExpanded = !val;
      this.localCommentaryForecastExpanded = !val;
    },
    isLocalCommentaryReasonCollapsed(val) {
      this.localApprovalReasonExpanded = !val;
    },
    validationErrorField(val) {
      if (val) {
        this.handleValidationError(val);
      }
    },
  },
  methods: {
    handleValidationError(field) {
      if (field === 'local_approval_reason') {
         this.$set(this.persistentErrors, field, 'Reason is required.');
      } else {
         this.$set(this.persistentErrors, field, 'Minimum 15 characters required.');
      }

      // 1. Expand the relevant section
      if (field === 'local_proforma') this.localCommentaryProformaExpanded = true;
      if (field === 'local_forecast') this.localCommentaryForecastExpanded = true;
      if (field === 'local_approval_reason') this.localApprovalReasonExpanded = true;
      if (field === 'regional_proforma') this.regionalCommentaryProformaExpanded = true;
      if (field === 'regional_forecast') this.regionalCommentaryForecastExpanded = true;
      if (field === 'regional_approval_reason') this.regionalApprovalReasonExpanded = true;

      // 2. Trigger shake animation
      this.$set(this.shakingFields, field, true);
      setTimeout(() => {
        this.$set(this.shakingFields, field, false);
      }, 500);

      // 3. Scroll to element
      this.$nextTick(() => {
        const refName = `${field}_section`;
        const element = Array.isArray(this.$refs[refName]) ? this.$refs[refName][0] : this.$refs[refName];
        if (element) {
           // Find a scrollable container
          let container = element.offsetParent;
          // Traverse up to find the scrollable container
          while (container) {
            const style = window.getComputedStyle(container);
            if (/(auto|scroll)/.test(style.overflowY) && container.scrollHeight > container.clientHeight) {
              break;
            }
            container = container.parentElement;
          }

          if (container) {
            // Scroll the container
             container.scrollTo({
              top: element.offsetTop - container.offsetTop - 20, // Adjust offset as needed
              behavior: 'smooth'
            });
          } else {
             // Fallback: Use scrollIntoView with 'nearest' or 'center' but wary of body scroll
             // strict block: 'nearest' often avoids global shifts
             element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      });
    },
    handleGenerateAi(slot, mode) {
      this.$emit('generate-ai', { commentary: this.selectedcommentary, slot, mode });
    },
    handleAttachmentChange() {
      this.$emit('attachment-change', this.selectedcommentary._id);
    },
    normalizeCommentaryText(value) {
      return String(value ?? '').trim();
    },
    ensureSubmittedCommentarySnapshots() {
      const commentary = this.selectedcommentary;
      if (!commentary || typeof commentary !== 'object') {
        return;
      }

      const assignIfMissing = (key, value) => {
        if (!Object.prototype.hasOwnProperty.call(commentary, key)) {
          this.$set(commentary, key, value);
        }
      };

      assignIfMissing('__submittedCommentary1', commentary.commentary1);
      assignIfMissing('__submittedCommentary2', commentary.commentary2);
      assignIfMissing('__submittedRegionalCommentary1', commentary.regionalcommentary1);
      assignIfMissing('__submittedRegionalCommentary2', commentary.regionalcommentary2);
    },
    scheduleDisplayBaselineCapture() {
      const commentaryId = this.selectedcommentary?._id || '';
      if (this.displayBaselinesReady && this.displayBaselinesCommentaryId === commentaryId) {
        return;
      }
      this.displayBaselinesCommentaryId = commentaryId;
      this.displayBaselinesReady = false;
      this.refreshCommentaryDirtyState();
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.captureDisplayBaselines();
        });
      });
    },
    captureDisplayBaselines() {
      const commentary = this.selectedcommentary || {};
      this.displayBaselines = {
        commentary1: this.normalizeCommentaryText(commentary.commentary1),
        commentary2: this.normalizeCommentaryText(commentary.commentary2),
        approvereason: this.normalizeCommentaryText(commentary.approvereason),
        regionalcommentary1: this.normalizeCommentaryText(commentary.regionalcommentary1),
        regionalcommentary2: this.normalizeCommentaryText(commentary.regionalcommentary2),
        regionalapprovereason: this.normalizeCommentaryText(commentary.regionalapprovereason),
      };
      this.displayBaselinesReady = true;
      this.refreshCommentaryDirtyState();
      this.refreshLocalCommentarySaveStates();
      this.refreshRegionalCommentarySaveStates();
      this.$emit('commentary-display-ready');
    },
    refreshLocalCommentarySaveStates() {
      this.localCommentary1Saved = this.isLocalCommentaryDraftSaved('commentary1', 'savedcommentary1');
      this.localCommentary2Saved = this.isLocalCommentaryDraftSaved('commentary2', 'savedcommentary2');
      this.localCommentary1CanSave = this.canSaveCommentaryField('commentary1');
      this.localCommentary2CanSave = this.canSaveCommentaryField('commentary2');
    },
    refreshRegionalCommentarySaveStates() {
      this.regionalCommentary1Saved = this.isRegionalCommentaryDraftSaved('regionalcommentary1', 'savedregionalcommentary1');
      this.regionalCommentary2Saved = this.isRegionalCommentaryDraftSaved('regionalcommentary2', 'savedregionalcommentary2');
      this.regionalCommentary1CanSave = this.canSaveRegionalCommentaryField('regionalcommentary1');
      this.regionalCommentary2CanSave = this.canSaveRegionalCommentaryField('regionalcommentary2');
    },
    refreshCommentaryDirtyState() {
      Object.keys(this.commentaryFieldDirty).forEach((field) => {
        if (!this.displayBaselinesReady) {
          this.$set(this.commentaryFieldDirty, field, false);
          return;
        }
        const current = this.normalizeCommentaryText(this.selectedcommentary?.[field]);
        const baseline = this.normalizeCommentaryText(this.displayBaselines?.[field]);
        this.$set(this.commentaryFieldDirty, field, current !== baseline);
      });
    },
    isCommentaryFieldDirty(current, baselineField) {
      if (!this.displayBaselinesReady) {
        return false;
      }
      if (Object.prototype.hasOwnProperty.call(this.commentaryFieldDirty, baselineField)) {
        return Boolean(this.commentaryFieldDirty[baselineField]);
      }
      const currentValue = this.normalizeCommentaryText(current);
      const baselineValue = this.normalizeCommentaryText(this.displayBaselines?.[baselineField]);
      return currentValue !== baselineValue;
    },
    isCommentaryFieldFilled(value) {
      return this.normalizeCommentaryText(value).length > 0;
    },
    canSaveCommentaryField(fieldName) {
      if (this.isLocalCommentaryFieldSubmitted(fieldName)) {
        return false;
      }

      if (!this.isCommentaryFieldFilled(this.selectedcommentary?.[fieldName])) {
        return false;
      }

      const savedFieldMap = {
        commentary1: 'savedcommentary1',
        commentary2: 'savedcommentary2',
      };
      const savedField = savedFieldMap[fieldName];

      if (savedField) {
        const current = this.normalizeCommentaryText(this.selectedcommentary?.[fieldName]);
        const saved = this.normalizeCommentaryText(this.selectedcommentary?.[savedField]);
        return current !== saved;
      }

      return this.isCommentaryFieldDirty(
        this.selectedcommentary?.[fieldName],
        fieldName
      );
    },
    isLocalCommentaryDraftSaved(commentaryField, savedField) {
      if (this.isLocalCommentaryFieldSubmitted(commentaryField)) {
        return true;
      }

      const saved = this.normalizeCommentaryText(this.selectedcommentary?.[savedField]);
      const current = this.normalizeCommentaryText(this.selectedcommentary?.[commentaryField]);

      if (!saved) {
        return false;
      }

      // Draft exists in DB; empty textarea still counts as saved draft on file.
      if (!current) {
        return true;
      }

      return current === saved;
    },
    isLocalCommentaryFieldSubmitted(commentaryField) {
      const submittedKey = commentaryField === 'commentary1'
        ? '__submittedCommentary1'
        : '__submittedCommentary2';

      if (!Object.prototype.hasOwnProperty.call(this.selectedcommentary || {}, submittedKey)) {
        return false;
      }

      return Boolean(this.normalizeCommentaryText(this.selectedcommentary[submittedKey]));
    },
    canSaveRegionalCommentaryField(fieldName) {
      if (this.isRegionalCommentaryFieldSubmitted(fieldName)) {
        return false;
      }

      if (!this.isCommentaryFieldFilled(this.selectedcommentary?.[fieldName])) {
        return false;
      }

      const savedFieldMap = {
        regionalcommentary1: 'savedregionalcommentary1',
        regionalcommentary2: 'savedregionalcommentary2',
      };
      const savedField = savedFieldMap[fieldName];

      if (savedField) {
        const current = this.normalizeCommentaryText(this.selectedcommentary?.[fieldName]);
        const saved = this.normalizeCommentaryText(this.selectedcommentary?.[savedField]);
        return current !== saved;
      }

      return this.isCommentaryFieldDirty(
        this.selectedcommentary?.[fieldName],
        fieldName
      );
    },
    isRegionalCommentaryFieldSubmitted(commentaryField) {
      const submittedKey = commentaryField === 'regionalcommentary1'
        ? '__submittedRegionalCommentary1'
        : '__submittedRegionalCommentary2';

      if (!Object.prototype.hasOwnProperty.call(this.selectedcommentary || {}, submittedKey)) {
        return false;
      }

      return Boolean(this.normalizeCommentaryText(this.selectedcommentary[submittedKey]));
    },
    isRegionalCommentaryDraftSaved(commentaryField, savedField) {
      if (this.isRegionalCommentaryFieldSubmitted(commentaryField)) {
        return true;
      }

      const saved = this.normalizeCommentaryText(this.selectedcommentary?.[savedField]);
      const current = this.normalizeCommentaryText(this.selectedcommentary?.[commentaryField]);

      if (!saved) {
        return false;
      }

      // Draft exists in DB; empty textarea still counts as saved draft on file.
      if (!current) {
        return true;
      }

      return current === saved;
    },
    /**
     * Safely display JV commentary whether it's already a formatted string or still an object.
     */
    displayJvCommentary(val) {
      if (!val) return '';
      if (typeof val === 'string') return val;
      return Object.entries(val).map(function(entry) { return entry[0] + ': ' + entry[1]; }).join('\n');
    },
    /**
     * Pre-populate commentary1/commentary2 with formatted JV text for local preparers.
     * Mirrors the old openSubmitDialog → handleAddLocalCommentaryDataSetup logic.
     * Only runs when the preparer opens a GL for local commentary input.
     */
    prefillJVCommentary() {
      // Format helper for JV commentary objects
      const formatJv = (jv) => {
        if (typeof jv === 'object' && jv !== null) {
          return Object.entries(jv).map(function(entry) { return entry[0] + ': ' + entry[1]; }).join('\n');
        }
        return jv || '';
      };

      const isLocalAction = !this.bulksubmit && (
        this.selectedcommentary.__myaction === 'localadd' ||
        this.selectedcommentary.__myaction === 'localvoluntary'
      ) && this.selectedcommentary.__stage !== 9;

      const isViewMode = this.commentarySubmitDialogueViewMode;

      // Scenario 3: Both workflows disabled — show JV in local commentary boxes
      if (!this.workflows.isworkflowsenabled) {
        this.selectedcommentary.commentary1 = formatJv(this.selectedcommentary.jv_commentary1) || this.selectedcommentary.savedcommentary1 || '';
        this.selectedcommentary.commentary2 = formatJv(this.selectedcommentary.jv_commentary2) || this.selectedcommentary.savedcommentary2 || '';
        return;
      }

      // View mode: format JV for display in commentary fields when stage_local is 0
      if (isViewMode) {
        if (this.selectedcommentary.stage_local === 0) {
          const fmtJv1 = formatJv(this.selectedcommentary.jv_commentary1);
          const fmtJv2 = formatJv(this.selectedcommentary.jv_commentary2);
          this.selectedcommentary.commentary1 = fmtJv1 || this.selectedcommentary.savedcommentary1 || this.selectedcommentary.commentary1 || '';
          this.selectedcommentary.commentary2 = fmtJv2 || this.selectedcommentary.savedcommentary2 || this.selectedcommentary.commentary2 || '';
        } else {
          // For other stages, use savedcommentary as fallback if commentary is empty
          this.selectedcommentary.commentary1 = this.selectedcommentary.commentary1 || this.selectedcommentary.savedcommentary1 || '';
          this.selectedcommentary.commentary2 = this.selectedcommentary.commentary2 || this.selectedcommentary.savedcommentary2 || '';
        }
        return;
      }

      // Scenario 1 & 4: Local enabled — pre-populate local commentary with JV when stage is valid for JV pre-population
      // Valid stages: 0 (Awaiting), 3 (Nil Value), 4 (Immaterial Variance), 5 (Not In Scope)
      if (isLocalAction) {
        const validStagesForJV = [0, 3, 4, 5];
        const formattedJv1 = formatJv(this.selectedcommentary.jv_commentary1);
        const formattedJv2 = formatJv(this.selectedcommentary.jv_commentary2);
        // Note: check formattedJv1 (not jv_commentary1 directly) — empty object {} is truthy but formats to ''
        if (this.workflows.islocalenabled && validStagesForJV.includes(this.selectedcommentary.stage_local) && formattedJv1) {
          this.selectedcommentary.commentary1 = formattedJv1;
          this.selectedcommentary.commentary2 = formattedJv2 || this.selectedcommentary.savedcommentary2 || this.selectedcommentary.commentary2 || '';
        } else {
          // Show saved or existing local commentary
          this.selectedcommentary.commentary1 = this.selectedcommentary.savedcommentary1 || this.selectedcommentary.commentary1 || '';
          this.selectedcommentary.commentary2 = this.selectedcommentary.savedcommentary2 || this.selectedcommentary.commentary2 || '';
        }

        // Clear fields if specific local workflow directions are disabled
        if (!this.workflows.islocalproformaenabled && (this.workflows.islocalenabled || this.workflows.isregionalenabled)) {
          this.selectedcommentary.commentary1 = '';
        }
        if (!this.workflows.islocalforecastenabled && (this.workflows.islocalenabled || this.workflows.isregionalenabled)) {
          this.selectedcommentary.commentary2 = '';
        }
      } else if (!this.bulksubmit) {
        // Path 4: __myaction is not localadd/localvoluntary (e.g. approver viewing)
        // Apply savedcommentary as fallback when commentary is empty
        if (!this.selectedcommentary.commentary1 && this.selectedcommentary.savedcommentary1) {
          this.selectedcommentary.commentary1 = this.selectedcommentary.savedcommentary1;
        }
        if (!this.selectedcommentary.commentary2 && this.selectedcommentary.savedcommentary2) {
          this.$set(this.selectedcommentary, 'commentary2', this.selectedcommentary.savedcommentary2);
        }
      }

      this.hydrateLocalSavedCommentaryDraft();
    },
    hydrateLocalSavedCommentaryDraft() {
      const pairs = [
        ['commentary1', 'savedcommentary1', this.workflows.lcp || this.workflows.lmp],
        ['commentary2', 'savedcommentary2', this.workflows.lcf || this.workflows.lmf],
      ];

      pairs.forEach(([commentaryField, savedField, workflowEnabled]) => {
        if (!workflowEnabled) {
          return;
        }
        const current = this.normalizeCommentaryText(this.selectedcommentary?.[commentaryField]);
        const saved = this.normalizeCommentaryText(this.selectedcommentary?.[savedField]);
        if (!current && saved) {
          this.$set(this.selectedcommentary, commentaryField, saved);
        }
      });
    },
    /**
     * Pre-populate regional commentary for local approvers acting as regional preparers.
     * NOTE: Does NOT mutate jv_commentary objects - keeps them for validation.
     */ 
    prefillRegionalCommentary() {
      const isRegionalAction = !this.bulksubmit && (
        this.selectedcommentary.__myaction === 'regionaladd' ||
        this.selectedcommentary.__myaction === 'regionalvoluntary'
      ) && this.selectedcommentary.__stage !== 9;

      if (!isRegionalAction || !this.isRegionalPreparer) return;

      // Format helper for JV commentary objects
      const formatJv = (jv) => {
        if (typeof jv === 'object' && jv !== null) {
          return Object.entries(jv).map(function(entry) { return entry[0] + ': ' + entry[1]; }).join('\n');
        }
        return jv || '';
      };

      const stageRegional = this.selectedcommentary.stage_regional ?? 0;
      const approvalStr = this.selectedcommentary.approvereason || '';

      // Scenario 2: Local disabled, Regional enabled — show JV in regional commentary boxes
      // Valid stages: 0 (Awaiting), 3 (Nil Value), 4 (Immaterial Variance), 5 (Not In Scope)
      const validStagesForRegionalJV = [0, 3, 4, 5];
      if (!this.workflows.islocalenabled && this.workflows.isregionalenabled && validStagesForRegionalJV.includes(stageRegional)) {
        if (this.selectedcommentary.jv_commentary1) {
          this.selectedcommentary.regionalcommentary1 = formatJv(this.selectedcommentary.jv_commentary1);
        }
        if (this.selectedcommentary.jv_commentary2) {
          this.selectedcommentary.regionalcommentary2 = formatJv(this.selectedcommentary.jv_commentary2);
        }
      } else {
        // Scenario 4: Both enabled — copy local commentary + approval reason to regional boxes
        const isEmptyRegional = (value) => !this.normalizeCommentaryText(value);
        if (isEmptyRegional(this.selectedcommentary.regionalcommentary1)) {
          if (this.selectedcommentary.commentary1 && this.workflows.isregionalproformaenabled) {
            this.selectedcommentary.regionalcommentary1 = this.selectedcommentary.commentary1 + '. ' + approvalStr;
          } else if (this.workflows.isregionalproformaenabled) {
            this.selectedcommentary.regionalcommentary1 = approvalStr;
          } else {
            this.selectedcommentary.regionalcommentary1 = '';
          }
        }
        if (isEmptyRegional(this.selectedcommentary.regionalcommentary2)) {
          if (this.selectedcommentary.commentary2 && this.workflows.isregionalforecastenabled) {
            this.selectedcommentary.regionalcommentary2 = this.selectedcommentary.commentary2 + '. ' + approvalStr;
          } else if (this.workflows.isregionalforecastenabled) {
            this.selectedcommentary.regionalcommentary2 = approvalStr;
          } else {
            this.selectedcommentary.regionalcommentary2 = '';
          }
        }

        // Clear if regional workflow directions are disabled
        if (!this.workflows.isregionalproformaenabled) {
          this.selectedcommentary.regionalcommentary1 = '';
        }
        if (!this.workflows.isregionalforecastenabled) {
          this.selectedcommentary.regionalcommentary2 = '';
        }

        // Special case: commentary2 with no threshold crossed
        if (isEmptyRegional(this.selectedcommentary.regionalcommentary2) &&
            this.selectedcommentary.commentary2 &&
            !this.selectedcommentary.var2_cross_control &&
            !this.selectedcommentary.var2_cross_management &&
            !this.selectedcommentary.var2_cross_management_regional &&
            !this.selectedcommentary.var2_cross_control_regional
        ) {
          this.selectedcommentary.regionalcommentary2 = this.selectedcommentary.commentary2;
          if (typeof approvalStr === 'string' && approvalStr.trim() !== '') {
            this.selectedcommentary.regionalcommentary2 += '  ' + approvalStr;
          }
        }
      }
      this.hydrateRegionalSavedCommentaryDraft();
    },
    hydrateRegionalSavedCommentaryDraft() {
      const pairs = [
        ['regionalcommentary1', 'savedregionalcommentary1', this.workflows.rcp || this.workflows.rmp],
        ['regionalcommentary2', 'savedregionalcommentary2', this.workflows.rcf || this.workflows.rmf],
      ];

      pairs.forEach(([commentaryField, savedField, workflowEnabled]) => {
        if (!workflowEnabled) {
          return;
        }
        const current = this.normalizeCommentaryText(this.selectedcommentary?.[commentaryField]);
        const saved = this.normalizeCommentaryText(this.selectedcommentary?.[savedField]);
        if (!current && saved) {
          this.$set(this.selectedcommentary, commentaryField, saved);
        }
      });
    },
    /**
     * Initialize section expanded/collapsed states based on user role and context
     */
    initializeSectionStates() {
      if (this.isLocalPreparerBulk) {
        // Collapse non-relevant sections for local preparer bulk commentary
        this.localApprovalReasonExpanded = false;
        this.regionalCommentaryProformaExpanded = false;
        this.regionalCommentaryForecastExpanded = false;
        this.regionalApprovalReasonExpanded = false;
      } else if (this.isLocalApproverBulk) {
        // Collapse non-relevant sections for local approver bulk approval
        // Only Local Approval/Rejection Reason should be expanded
        this.localCommentaryProformaExpanded = false;
        this.localCommentaryForecastExpanded = false;
        this.regionalCommentaryProformaExpanded = false;
        this.regionalCommentaryForecastExpanded = false;
        this.regionalApprovalReasonExpanded = false;
        // Keep local approval reason expanded
        this.localApprovalReasonExpanded = true;
      }
    },
  },
};
</script>

<style scoped>
.border-left-custom {
  border-left: 4px solid;
  border-left-color: var(--custom-border-color);
}

.noTransform {
  text-transform: none;
}

.cursor-pointer {
  cursor: pointer;
}

.accordion-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s ease;
}

.accordion-section:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.accordion-header {
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
  min-height: 44px;
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease-in-out !important;
}

.accordion-content {
  transition: all 0.3s ease-in-out;
}

.accordion-header .v-icon {
  transition: transform 0.3s ease-in-out;
}

.commentary-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  color: #333;
}

.accordion-content .commentary1-textarea ::v-deep .v-input__slot {
  min-height: 120px;
}

.accordion-content .commentary1-textarea ::v-deep textarea {
  min-height: 100px;
}

.accordion-content > * {
  transition: opacity 0.2s ease-in-out 0.1s;
}

/* Disabled section styles for role-based visibility */
.section-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.accordion-disabled {
  background-color: #f5f5f5;
  border-color: #e0e0e0;
}

.accordion-disabled .accordion-header {
  background-color: #fafafa;
  color: #9e9e9e;
}

.accordion-disabled .accordion-header .font-weight-semibold {
  color: #9e9e9e;
}

.accordion-disabled .accordion-header .v-icon {
  color: #bdbdbd !important;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

/* Re-enable pointer events on the disabled section wrapper but keep visual disabled */
.section-disabled.accordion-section {
  pointer-events: auto;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

.shake-error {
  animation: shake 0.5s;
}

.error-highlight ::v-deep .v-input__slot,
.error-highlight ::v-deep fieldset {
  border-color: #ff5252 !important;
  border-width: 2px !important;
}

.regional-reason-save-card {
  padding-bottom: var(--space-12, 40px);
}

.regional-reason-save-btn,
.commentary-save-btn {
  position: absolute;
  right: var(--space-4, 8px);
  bottom: var(--space-4, 8px);
  z-index: 1;
}

.commentary-save-card {
  padding-bottom: var(--space-12, 40px);
  width: 100%;
}



</style>
